import { Suspense, lazy } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { AuthProvider } from '../shared/auth/AuthProvider.jsx';
import { AnonymousRoute, ProtectedRoute } from '../shared/auth/route-guards.jsx';
import { Layout } from './Layout.jsx';
import { Cargador } from '../shared/ui/Cargador.jsx';
import { Home } from '../features/home/Home.jsx';

/**
 * Tabla de rutas.
 *
 * El orden importa: las rutas concretas van antes que las que contienen
 * comodines, y `path="*"` va SIEMPRE la última. Si el 404 se declarara antes,
 * se comería todas las rutas siguientes.
 *
 * El agrupamiento decide quién ve qué:
 *   · `/` y `/programa`  público
 *   · login, registro y recuperación  solo anónimos (con redirección si ya hay sesión)
 *   · el resto     con sesión, dentro del Layout
 *
 * ── Por qué el resto van con `lazy` ─────────────────────────────────────────
 *
 * La portada se carga de una: es lo primero que ve quien llega y no puede
 * quedarse en blanco. El resto de páginas se piden a demanda, cada una en su
 * trozo de JavaScript. Así el paquete inicial no arrastra el editor de código
 * de las lecciones (CodeMirror son cientos de kB), que solo hace falta dentro de
 * una lección. Navegar a una página carga su trozo una vez y luego queda en
 * caché; React lo resuelve dentro del Suspense de abajo.
 */
const Program = lazy(() =>
  import('../features/program/Program.jsx').then((m) => ({ default: m.Program })),
);
const PlanDeEstudios = lazy(() =>
  import('../features/plan/PlanDeEstudios.jsx').then((m) => ({ default: m.PlanDeEstudios })),
);
const Recursos = lazy(() =>
  import('../features/recursos/Recursos.jsx').then((m) => ({ default: m.Recursos })),
);
const Login = lazy(() =>
  import('../features/auth/Login.jsx').then((m) => ({ default: m.Login })),
);
const Register = lazy(() =>
  import('../features/auth/Register.jsx').then((m) => ({ default: m.Register })),
);
const ForgotPassword = lazy(() =>
  import('../features/auth/ForgotPassword.jsx').then((m) => ({ default: m.ForgotPassword })),
);
const Dashboard = lazy(() =>
  import('../features/dashboard/Dashboard.jsx').then((m) => ({ default: m.Dashboard })),
);
const Lessons = lazy(() =>
  import('../features/lessons/Lessons.jsx').then((m) => ({ default: m.Lessons })),
);
const LessonDetail = lazy(() =>
  import('../features/lessons/LessonDetail.jsx').then((m) => ({ default: m.LessonDetail })),
);
const Projects = lazy(() =>
  import('../features/projects/Projects.jsx').then((m) => ({ default: m.Projects })),
);
const Profile = lazy(() =>
  import('../features/account/Profile.jsx').then((m) => ({ default: m.Profile })),
);
const Security = lazy(() =>
  import('../features/account/Security.jsx').then((m) => ({ default: m.Security })),
);
const NotFound = lazy(() =>
  import('../features/home/NotFound.jsx').then((m) => ({ default: m.NotFound })),
);

export function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        {/* Cubre las páginas públicas y de sesión, que no van dentro del Layout. */}
        <Suspense fallback={<Cargador texto="Cargando…" />}>
          <Routes>
            <Route path="/" element={<Home />} />
            {/* Público como la portada, y por el mismo motivo: el programa se lee
                sin sesión y es lo que convence a quien llega de fuera. Abrir una
                lección ya pide cuenta, y eso lo decide el guard de abajo. */}
            <Route path="/programa" element={<Program />} />
            {/* Público como el programa: el plan entero es lo que convence a
                quien todavía duda de que el camino llegue hasta el final. */}
            <Route path="/plan-de-estudios" element={<PlanDeEstudios />} />
            {/* Público como el programa: una lista de enlaces externos no necesita
                cuenta para ser útil, y el que llega de un buscador también la mira. */}
            <Route path="/recursos" element={<Recursos />} />

            <Route element={<AnonymousRoute />}>
              <Route path="/login" element={<Login />} />
              <Route path="/registro" element={<Register />} />
              {/* La recuperación no tiene sesión por definición: pedir la contraseña
                  nueva es exactamente lo que NO puede hacer alguien logueado. */}
              <Route path="/recuperar" element={<ForgotPassword />} />
            </Route>

            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/panel" element={<Dashboard />} />
                <Route path="/lecciones" element={<Lessons />} />
                {/* El `:lessonId` tiene que declararse DESPUÉS de `/lecciones`.
                    React Router ordena por puntuación y el comodín está bien
                    colocado, pero dejarlo explícito evita que alguien mueva la
                    ruta y rompa el listado sin darse cuenta. */}
                <Route path="/lecciones/:lessonId" element={<LessonDetail />} />
                <Route path="/proyectos" element={<Projects />} />
                <Route path="/cuenta" element={<Profile />} />
                <Route path="/cuenta/seguridad" element={<Security />} />
              </Route>
            </Route>

            {/* Cualquier ruta desconocida. */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}
